import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Breadcrumbs,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TestResultsPageStyles from "./TestResultsPage.styles";
import { PATHS } from "../../../../constants/pathConstants";
import PageTitle from "../../../../utils/PageTitle";
import {
  selectExistingOrders,
  selectRiskAssessments
} from "../../../../store/reducers/order/OrderInformationSlice";
import { useSelector } from "react-redux";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FileListing from "./FileListing";
import {
  getDescriptionLanguageForTestByTestDescription,
  getFaqLanguageForTestByTestDescription,
  getTestNameFromOrderDescription
} from "./testLanguage";
import { useParams, useHistory, useLocation } from "react-router-dom";
import PositiveStrandIcon from "../../../../assets/icons/results-positive-strand.svg";
import PotentiallyPositiveStrandIcon from "../../../../assets/icons/results-potentially-positive-strand.svg";
import CarrierStrandIcon from "../../../../assets/icons/results-carrier-strand.svg";
import NegativeStrandIcon from "../../../../assets/icons/results-negative-strand.svg";
import DownloadIcon from "../../../../assets/icons/results-action-download.svg";
import TalkIcon from "../../../../assets/icons/results-action-talk.svg";
import ShareIcon from "../../../../assets/icons/results-action-share.svg";
import QuestionsIcon from "../../../../assets/icons/results-action-questions.svg";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import { useEffect } from "react";

export const RESULTS_CONFIGS = {
  positive: {
    icon: PositiveStrandIcon,
    description:
      "This means that the test found a change in at least one of the genes tested that raises your chances of getting a type of cancer or other health condition. This result means that you are at higher risk to get a certain type of condition than the general population. More frequent health checkups and prevention strategies can help.",
    subText: "We will reach out shortly to discuss your results."
  },
  "increased risk": {
    icon: PositiveStrandIcon,
    description:
      "This means that the test found a change in at least one of the genes tested that raises your chances of getting a type of cancer or other health condition. This result means that you are at higher risk to get a certain type of condition than the general population. More frequent health checkups and prevention strategies can help.",
    subText: "We will reach out shortly to discuss your results."
  },
  "potentially positive": {
    icon: PotentiallyPositiveStrandIcon,
    description:
      "This means the test found something that needs follow up testing. You could be at risk for a condition, but the laboratory needs more information to be able to confirm this. They might need to test one or more members of your family to help determine your personal risk.",
    subText: "We will reach out shortly to discuss your results."
  },
  carrier: {
    icon: CarrierStrandIcon,
    description:
      "This means the test found a change in a gene(s) that will not raise your risk of disease, but that could be passed on to your children. No other changes were found with this test that could impact your personal risk of disease.",
    subText:
      "Your lifestyle and personal/familial health history play a big part in your risk level. The results only provide information regarding any changes in your genes."
  },
  negative: {
    icon: NegativeStrandIcon,
    description:
      "This means that no changes were found in the tested genes that are known to increase risk of certain conditions.",
    subText:
      "Your lifestyle and personal/familial health history play a big part in your risk level. The results only provide information regarding any changes in your genes."
  }
};

const TestResultsPage = (props) => {
  const classes = TestResultsPageStyles();
  const { glOrderId } = useParams();
  const location = useLocation();
  const order = useSelector(selectExistingOrders).find(
    (order) => order.glOrderId === glOrderId
  );
  const history = useHistory();
  const theme = useTheme();
  const screenWidthIsMdOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));

  useEffect(() => {
    if (location.hash === "#fileListingContainer") {
      const element = document.getElementById("fileListingContainer");
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  PageTitle("Test results");

  const riskAssessment = useSelector(selectRiskAssessments);

  const getResultConfig = (result) => {
    return (
      RESULTS_CONFIGS[result.toLowerCase()] || { icon: null, description: "" }
    );
  };

  const renderFAQ = () => {
    return getFaqLanguageForTestByTestDescription(order.description).map(
      (line) => {
        return (
          <Accordion className={classes.faqAccordian} key={line.question}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant={"body_strong_black"}>
                <div dangerouslySetInnerHTML={{ __html: line.question }} />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant={"body_dark_gray"}>
                <div dangerouslySetInnerHTML={{ __html: line.answer }} />
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      }
    );
  };

  const getWhatsNextBox = (smallMode = false) => (
    <Grid
      item
      sm={smallMode ? 4 : 12}
      className={`${smallMode ? classes.sideMenuContainer : ""}`}
    >
      <Grid container>
        <Grid item>
          <Grid container direction={"column"}>
            <Typography variant="h4_accent2">Your next actions</Typography>
            <Grid
              container
              direction="column"
              spacing={2}
              className={classes.nextActions}
            >
              <Grid item container spacing={2} alignItems="center">
                <Grid item>
                  <img
                    src={DownloadIcon}
                    alt="Download"
                    className={classes.actionIcon}
                  />
                </Grid>
                <Grid item xs>
                  <Typography
                    variant={
                      smallMode ? "body_strong_black" : "body_strong_lg_black"
                    }
                  >
                    Download your results to share with your healthcare
                    provider.
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container spacing={2} alignItems="center">
                <Grid item>
                  <img
                    src={TalkIcon}
                    alt="Talk"
                    className={classes.actionIcon}
                  />
                </Grid>
                <Grid item xs>
                  <Typography
                    variant={
                      smallMode ? "body_strong_black" : "body_strong_lg_black"
                    }
                  >
                    Talk with your healthcare provider(s) about what you should
                    do next for your health screenings and create a prevention
                    plan.
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container spacing={2} alignItems="center">
                <Grid item>
                  <img
                    src={ShareIcon}
                    alt="Share"
                    className={classes.actionIcon}
                  />
                </Grid>
                <Grid item xs>
                  <Typography
                    variant={
                      smallMode ? "body_strong_black" : "body_strong_lg_black"
                    }
                  >
                    Share your test results with your family because they may
                    also be at risk. Information about family testing can be
                    found at{" "}
                    <a
                      className={classes.faqLink}
                      href="https://www.invitae.com/us/providers/follow-up-testing"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Invitae
                    </a>
                    .
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container spacing={2} alignItems="center">
                <Grid item>
                  <img
                    src={QuestionsIcon}
                    alt="Questions"
                    className={classes.actionIcon}
                  />
                </Grid>
                <Grid item xs>
                  <Typography
                    variant={
                      smallMode ? "body_strong_black" : "body_strong_lg_black"
                    }
                  >
                    Have more questions?{" "}
                    <a href="#faq" className={classes.faqLink}>
                      Click here
                    </a>{" "}
                    to read some questions others have asked us.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderResultsBox = () => {
    if (order.glTestType === "GHS") {
      return (
        <>
          <Grid item xs={12} className={classes.riskAssessmentContainer}>
            <Grid container direction={"column"}>
              <Grid item>
                <img
                  className={classes.resultIcon}
                  src={getResultConfig(riskAssessment[0].topLineResult).icon}
                  alt={`${riskAssessment[0].topLineResult} result icon`}
                />
              </Grid>
              <Grid item>
                <Typography variant="body_strong_lg2_black" component="span">
                  Your Results:{" "}
                </Typography>
                <Typography variant="body_strong_lg2_primary" component="span">
                  {riskAssessment[0].topLineResult.charAt(0).toUpperCase() +
                    riskAssessment[0].topLineResult.slice(1)}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="body_black"
                  className={classes.resultDescription}
                >
                  {getResultConfig(riskAssessment[0].topLineResult).description}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  sx={{
                    marginTop: "20px"
                  }}
                  variant="body_strong"
                  className={classes.faqLink}
                  onClick={() => {
                    history.push(PATHS.RESULTS_MORE_INFO_SLIDE);
                  }}
                >
                  Learn more about how to interpret your result
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Typography variant="body_dark_gray">
              If there is a finding on your report that may directly impact your
              health, a Navigator will be reaching out to you to review your
              result and discuss next steps. If you have further questions
              regarding the impact of your result, Navigation Services can be
              reached at{" "}
              <a
                href="mailto:navigation@genomiclife.com"
                className={classes.faqLink}
              >
                navigation@genomiclife.com
              </a>
              .
            </Typography>
          </Grid>
        </>
      );
    }
  };

  return (
    <div>
      <GradientFramedLayout showHeader={true}>
        <Grid container className={classes.container}>
          <Grid container item md={12} lg={8} justifyContent={"center"}>
            <Grid item xs={11} sm={11}>
              <Grid>
                <Typography variant="h4_primary">
                  {getTestNameFromOrderDescription(order.description)}
                </Typography>

                <Breadcrumbs
                  className={classes.breadcrumbs}
                  aria-label="breadcrumb"
                  separator={<NavigateNextIcon fontSize="small" />}
                >
                  <a href={PATHS.LANDING_PAGE}>
                    <Typography variant="body_dark_gray">Dashboard</Typography>
                  </a>
                  <Typography variant="body_dark_gray">
                    {getTestNameFromOrderDescription(order.description)}
                  </Typography>
                </Breadcrumbs>
                <Typography variant="body_black">
                  {getDescriptionLanguageForTestByTestDescription(
                    order.description
                  )}
                </Typography>
              </Grid>

              {renderResultsBox()}

              {screenWidthIsMdOrSmaller ? (
                <Grid className={classes.section}>
                  {getWhatsNextBox(false)}
                </Grid>
              ) : null}

              <Grid className={classes.section} id="fileListingContainer">
                <FileListing order={order} />
              </Grid>
              <Grid className={classes.section}>
                <Grid container>
                  <Grid item>
                    <Grid container direction={"column"}>
                      <Typography variant="h4_accent2">
                        Do you have questions?
                      </Typography>
                      <Typography
                        variant="body_dark_gray"
                        style={{ fontStyle: "italic" }}
                      >
                        Here are some common questions other members have asked
                        us.
                      </Typography>
                      <div id="faq">{renderFAQ()}</div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {screenWidthIsMdOrSmaller ? null : getWhatsNextBox(true)}
        </Grid>
      </GradientFramedLayout>
    </div>
  );
};

export default TestResultsPage;
